import { Component, input, output, signal, computed, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  DatePreset,
  DateRangeValue,
  DateRangeFilterConfig,
  DEFAULT_DATE_RANGE_CONFIG
} from './date-range-filter.model';

/**
 * Reusable Date Range Filter Component
 * 
 * @remarks
 * A standalone component for selecting date ranges using either predefined presets
 * or custom date selection. Supports validation, clearing, and full accessibility.
 * 
 * **Features:**
 * - Predefined date presets (Last 7 days, Last 30 days, etc.)
 * - Custom date range selection (from/to)
 * - Date validation (from <= to)
 * - Toggle preset on/off
 * - Clear filter functionality
 * - Light/dark mode support
 * - Keyboard accessible (WCAG 2.1 AA)
 * - Smooth animations
 * 
 * @example
 * ```html
 * <cms-date-range-filter
 *   [selectedPreset]="datePreset()"
 *   [dateFrom]="dateFrom()"
 *   [dateTo]="dateTo()"
 *   [label]="'Creation Date'"
 *   [presets]="DATE_PRESETS"
 *   (rangeChange)="onDateRangeChange($event)"
 *   (clear)="clearDateFilter()"
 * />
 * ```
 * 
 * @example
 * ```typescript
 * // In parent component
 * onDateRangeChange(range: DateRangeValue): void {
 *   if (range.isValid) {
 *     this.dateFrom.set(range.dateFrom);
 *     this.dateTo.set(range.dateTo);
 *     this.triggerSearch();
 *   }
 * }
 * ```
 */
@Component({
  selector: 'cms-date-range-filter',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './date-range-filter.component.html',
  styleUrls: ['./date-range-filter.component.css']
})
export class DateRangeFilterComponent implements OnInit {
  // ============================================================================
  // INPUTS
  // ============================================================================

  /** Currently selected preset identifier */
  selectedPreset = input<string | null>(null);

  /** Start date in ISO 8601 format (yyyy-MM-dd) */
  dateFrom = input<string | null>(null);

  /** End date in ISO 8601 format (yyyy-MM-dd) */
  dateTo = input<string | null>(null);

  /** Label displayed above the filter */
  label = input<string>(DEFAULT_DATE_RANGE_CONFIG.label);

  /** Available date presets for quick selection */
  presets = input<DatePreset[]>(DEFAULT_DATE_RANGE_CONFIG.presets);

  /** Whether to show the clear button */
  showClearButton = input<boolean>(DEFAULT_DATE_RANGE_CONFIG.showClearButton);

  /** Whether the filter is disabled */
  disabled = input<boolean>(DEFAULT_DATE_RANGE_CONFIG.disabled);

  /** Whether to show validation errors */
  showValidation = input<boolean>(DEFAULT_DATE_RANGE_CONFIG.showValidation);

  /** Custom validation error message */
  validationErrorMessage = input<string>(DEFAULT_DATE_RANGE_CONFIG.validationErrorMessage);

  // ============================================================================
  // OUTPUTS
  // ============================================================================

  /** Emitted when the date range changes (preset or custom dates) */
  rangeChange = output<DateRangeValue>();

  /** Emitted when the preset selection changes */
  presetChange = output<string | null>();

  /** Emitted when the dateFrom changes */
  dateFromChange = output<string | null>();

  /** Emitted when the dateTo changes */
  dateToChange = output<string | null>();

  /** Emitted when the clear button is clicked */
  clear = output<void>();

  // ============================================================================
  // INTERNAL STATE
  // ============================================================================

  /** Internal state for date from (for two-way binding) */
  protected internalDateFrom = signal<string>('');

  /** Internal state for date to (for two-way binding) */
  protected internalDateTo = signal<string>('');

  /** Whether the date range is valid */
  protected isValid = computed(() => this.validateDateRange());

  /** Whether any filter is active */
  protected hasActiveFilter = computed(() => {
    return !!(this.dateFrom() || this.dateTo() || this.selectedPreset());
  });

  /** Current date in ISO format (for max date validation) */
  protected readonly today = new Date().toISOString().split('T')[0];

  // ============================================================================
  // LIFECYCLE HOOKS
  // ============================================================================

  ngOnInit(): void {
    // Initialize internal state from inputs
    this.internalDateFrom.set(this.dateFrom() || '');
    this.internalDateTo.set(this.dateTo() || '');
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Handle preset button click
   * 
   * @param presetValue - The preset identifier
   * 
   * @remarks
   * If the same preset is clicked again, it will be toggled off.
   * Otherwise, the preset's date range is applied.
   */
  protected onPresetClick(presetValue: string): void {
    if (this.disabled()) return;

    // Toggle off if clicking the same preset
    if (this.selectedPreset() === presetValue && presetValue !== 'custom') {
      this.clearFilter();
      return;
    }

    // Handle custom preset - just set it, don't calculate dates
    if (presetValue === 'custom') {
      this.presetChange.emit('custom');
      this.emitRangeChange('custom', this.dateFrom(), this.dateTo());
      return;
    }

    // Find and apply the preset
    const preset = this.presets().find(p => p.value === presetValue);
    if (preset) {
      const { dateFrom, dateTo } = preset.getDateRange();
      
      this.internalDateFrom.set(dateFrom);
      this.internalDateTo.set(dateTo);
      
      this.presetChange.emit(presetValue);
      this.dateFromChange.emit(dateFrom);
      this.dateToChange.emit(dateTo);
      this.emitRangeChange(presetValue, dateFrom, dateTo);
    }
  }

  /**
   * Handle date from input change
   * 
   * @param value - The new date value
   */
  protected onDateFromInput(value: string): void {
    if (this.disabled()) return;

    const newValue = value || null;
    this.internalDateFrom.set(value);
    
    this.dateFromChange.emit(newValue);
    this.presetChange.emit('custom');
    this.emitRangeChange('custom', newValue, this.dateTo());
  }

  /**
   * Handle date to input change
   * 
   * @param value - The new date value
   */
  protected onDateToInput(value: string): void {
    if (this.disabled()) return;

    const newValue = value || null;
    this.internalDateTo.set(value);
    
    this.dateToChange.emit(newValue);
    this.presetChange.emit('custom');
    this.emitRangeChange('custom', this.dateFrom(), newValue);
  }

  /**
   * Clear the date filter
   */
  protected clearFilter(): void {
    if (this.disabled()) return;

    this.internalDateFrom.set('');
    this.internalDateTo.set('');
    
    this.presetChange.emit(null);
    this.dateFromChange.emit(null);
    this.dateToChange.emit(null);
    this.clear.emit();
    
    this.emitRangeChange(null, null, null);
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Validate the date range
   * 
   * @returns True if the date range is valid or no dates are set
   */
  private validateDateRange(): boolean {
    const from = this.dateFrom();
    const to = this.dateTo();

    // If neither date is set, consider it valid
    if (!from && !to) {
      return true;
    }

    // If only one date is set, it's valid
    if (!from || !to) {
      return true;
    }

    // Both dates are set - validate from <= to
    return new Date(from) <= new Date(to);
  }

  /**
   * Emit the complete range change event
   * 
   * @param preset - The selected preset (or 'custom' or null)
   * @param dateFrom - The start date
   * @param dateTo - The end date
   */
  private emitRangeChange(preset: string | null, dateFrom: string | null, dateTo: string | null): void {
    const rangeValue: DateRangeValue = {
      preset,
      dateFrom,
      dateTo,
      isValid: this.validateDateRange()
    };
    
    this.rangeChange.emit(rangeValue);
  }

  /**
   * Check if a preset is currently selected
   * 
   * @param presetValue - The preset identifier to check
   * @returns True if this preset is selected
   */
  protected isPresetSelected(presetValue: string): boolean {
    return this.selectedPreset() === presetValue;
  }
}
